import type { EditPlan, Rational, Scene } from '@estudio/contracts';

export type IssueSeverity = 'blocking' | 'warning';
export interface PlanIssue {
  severity: IssueSeverity;
  code:
    | 'SOUND_RANGE'
    | 'SCENE_OVERLAP'
    | 'SUBTITLE_IN_PROTECTED_ZONE'
    | 'ASSET_PENDING'
    | 'CHAPTER_RANGE'
    | 'SCENE_OUTSIDE_CHAPTER'
    | 'WINK_READING_TIME'
    | 'AVATAR_OCCUPANCY';
  scene_id?: string;
  message: string;
}

export function framesToSeconds(frames: number, fps: Rational): number {
  return (frames * fps.den) / fps.num;
}

/** Conversión solo para mostrar; nunca reescribe la fuente. */
export function formatFrames(frames: number, fps: Rational): string {
  const totalSeconds = framesToSeconds(frames, fps);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}

export function secondsToFrames(seconds: number, fps: Rational): number {
  return Math.round((seconds * fps.num) / fps.den);
}

export function planDurationFrames(plan: EditPlan): number {
  return plan.scenes.reduce((max, s) => Math.max(max, s.output.end_frame), 0);
}

function intersects(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Validación estructural del plan (la única QA que emite la parte A).
 * No afirma escucha, decodificación ni apertura nativa.
 */
export function validatePlan(
  plan: EditPlan,
  opts: { winkMinSeconds?: number; avatarMaxOccupancy?: number } = {},
): PlanIssue[] {
  const issues: PlanIssue[] = [];
  const main = plan.scenes.filter((s) => (s.track ?? 'main') === 'main').sort((a, b) => a.output.start_frame - b.output.start_frame || a.output.end_frame - b.output.end_frame);
  let maxEnd = -1;
  for (const s of main) {
    const overlap = maxEnd - s.output.start_frame;
    if (maxEnd >= 0 && overlap > (s.transition?.overlap_frames ?? 0)) {
      issues.push({ severity: 'blocking', code: 'SCENE_OVERLAP', scene_id: s.id, message: `La escena ${s.id} solapa ${overlap} fotogramas sin transición que lo declare.` });
    }
    maxEnd = Math.max(maxEnd, s.output.end_frame);
  }

  for(const scene of plan.scenes)for(const sound of scene.sound_actions??[]) {
    const start=sound.output_start_frame??scene.output.start_frame,end=sound.output_end_frame??scene.output.end_frame;
    if(end<=start||start<scene.output.start_frame||end>scene.output.end_frame)issues.push({severity:'blocking',code:'SOUND_RANGE',scene_id:scene.id,message:`El sonido de ${scene.id} debe quedar dentro de la escena y terminar después de su inicio.`});
  }
  const zones = plan.output.protected_zones ?? [];
  for (const s of plan.scenes) {
    for (const t of s.texts ?? []) {
      if (t.kind !== 'subtitle' || !t.zone) continue;
      const hit = zones.find((z) => intersects(t.zone!, z));
      if (hit) {
        issues.push({ severity: 'blocking', code: 'SUBTITLE_IN_PROTECTED_ZONE', scene_id: s.id, message: `Subtítulo de ${s.id} invade la zona protegida «${hit.id}» (${hit.reason}).` });
      }
    }
    for (const t of s.texts ?? []) {
      if (t.kind === 'wink' && opts.winkMinSeconds !== undefined) {
        const secs = framesToSeconds(s.output.end_frame - s.output.start_frame, plan.output.fps);
        if (secs < opts.winkMinSeconds) {
          issues.push({ severity: 'warning', code: 'WINK_READING_TIME', scene_id: s.id, message: `Guiño en ${s.id} dura ${secs.toFixed(2)} s (< ${opts.winkMinSeconds} s por calibrar).` });
        }
      }
    }
    if (s.avatar?.occupancy !== undefined && opts.avatarMaxOccupancy !== undefined && s.avatar.occupancy > opts.avatarMaxOccupancy) {
      issues.push({ severity: 'warning', code: 'AVATAR_OCCUPANCY', scene_id: s.id, message: `Ocupación del avatar ${s.avatar.occupancy} supera ${opts.avatarMaxOccupancy} (por calibrar).` });
    }
    if (!s.asset || s.asset.asset_version_id === null || s.asset.pending_slot) {
      if ((s.track ?? 'main') === 'main') {
        issues.push({ severity: 'warning', code: 'ASSET_PENDING', scene_id: s.id, message: `La escena ${s.id} no tiene recurso vinculado; no es renderizable todavía.` });
      }
    }
  }

  for (const ch of plan.chapters ?? []) {
    if (ch.end_frame <= ch.start_frame) {
      issues.push({ severity: 'blocking', code: 'CHAPTER_RANGE', message: `Capítulo ${ch.id} con rango inválido.` });
    }
  }
  const chapters = new Map((plan.chapters ?? []).map((c) => [c.id, c]));
  for (const s of plan.scenes) {
    if (!s.chapter_id) continue;
    const ch = chapters.get(s.chapter_id);
    if (!ch || s.output.start_frame < ch.start_frame || s.output.end_frame > ch.end_frame) {
      issues.push({ severity: 'warning', code: 'SCENE_OUTSIDE_CHAPTER', scene_id: s.id, message: `La escena ${s.id} queda fuera de su capítulo.` });
    }
  }
  return issues;
}

export function isRenderable(scene: Scene): boolean {
  return !!scene.asset && scene.asset.asset_version_id !== null && !scene.asset.pending_slot;
}

/** Ventanas de revisión de cinco minutos (no son unidades de render). */
export function reviewWindows(plan: EditPlan, minutes = 5): { index: number; start_frame: number; end_frame: number; scene_ids: string[] }[] {
  const total = planDurationFrames(plan);
  const size = secondsToFrames(minutes * 60, plan.output.fps);
  const windows = [];
  for (let start = 0, i = 0; start < total; start += size, i++) {
    const end = Math.min(start + size, total);
    windows.push({
      index: i,
      start_frame: start,
      end_frame: end,
      scene_ids: plan.scenes.filter((s) => s.output.start_frame < end && s.output.end_frame > start).map((s) => s.id),
    });
  }
  return windows;
}

/** Impacto posterior de cambiar la duración de una escena (escenas desplazadas). */
export function durationImpact(before: EditPlan, after: EditPlan): { scene_id: string; shift_frames: number }[] {
  const prev = new Map(before.scenes.map((s) => [s.id, s.output.start_frame]));
  return after.scenes
    .filter((s) => prev.has(s.id) && prev.get(s.id) !== s.output.start_frame)
    .map((s) => ({ scene_id: s.id, shift_frames: s.output.start_frame - prev.get(s.id)! }));
}

export function emptyPlan(format: 'long' | 'short', profileVersionId: string | null): EditPlan {
  const vertical = format === 'short';
  return {
    schema_version: 1,
    type: 'edit_plan',
    profile_version_id: profileVersionId,
    engine_required: { name: 'estudio-motor', min_version: '1.0.0' },
    output: {
      width: vertical ? 1080 : 1920,
      height: vertical ? 1920 : 1080,
      fps: { num: 30000, den: 1001 },
      sample_rate: 48000,
      orientation: vertical ? 'vertical' : 'horizontal',
      protected_zones: vertical ? [{ id: 'ui_inferior', x: 0, y: 0.82, w: 1, h: 0.18, reason: 'Interfaz de la plataforma' }] : [],
    },
    chapters: [],
    scenes: [],
    dependencies: [],
  };
}
